const DETAIL_NEEDED_STATUSES = new Set(['not_declared', 'needs_new_input']);
const REPLACEMENT_STATUSES = new Set(['replacement_requested', 'replacement_required']);
const WAITING_STATUSES = new Set(['sent', 'requested', 'awaiting_response', 'viewed']);
const RESPONSE_STATUSES = new Set(['responded', 'response_received', 'awaiting_review', 'pending_admin_review', 'reviewed', 'awaiting_verification']);

function lower(value) {
  return String(value || '').toLowerCase();
}

function hasEmail(referee = {}) {
  return Boolean(referee?.email || referee?.referee_email);
}

function statusFrom(reference = {}) {
  return lower(reference.lifecycle_status || reference.status || reference.request_status || reference.verification?.status);
}

function hasDifference(reference = {}) {
  const mismatchDetected = Boolean(
    reference.mismatch_detected
    || reference.integrity?.mismatch_detected
    || reference.mismatch?.detected
  );
  const mismatchResolved = Boolean(
    reference.mismatch_resolved
    || reference.integrity?.mismatch_resolved
    || reference.mismatch?.resolved
  );
  return Boolean(
    reference.counts_toward_readiness === false
    || (mismatchDetected && !mismatchResolved)
    || reference.requires_mismatch_explanation
    || reference.awaiting_worker_explanation
  );
}

function hasResponse(reference = {}) {
  return Boolean(reference.has_response || reference.response?.exists || reference.response_received_at);
}

function isVerified(reference = {}) {
  return Boolean(reference.verified || reference.verification?.verified || statusFrom(reference) === 'verified');
}

function isRejected(reference = {}) {
  const status = statusFrom(reference);
  return Boolean(reference.rejected || reference.verification?.rejected || status === 'rejected');
}

function needsReplacement(reference = {}) {
  const status = statusFrom(reference);
  return Boolean(
    reference.can_provide_new
    || reference.replacement_requested_at
    || reference.verification?.replacement_requested
    || REPLACEMENT_STATUSES.has(status)
  );
}

function detailsNeeded(reference = {}) {
  const status = statusFrom(reference);
  return Boolean(!reference.has_declared || DETAIL_NEEDED_STATUSES.has(status));
}

export function deriveReferencePresentation(reference = {}, options = {}) {
  const { isApplicant = false, isAuditor = false } = options;
  const status = statusFrom(reference);
  const declared = reference.declared_referee || reference.referee || {};
  const title = reference.title || `Reference ${reference.reference_num || reference.reference_number || ''}`.trim();

  // A stale/legacy verified flag must never override a live unresolved mismatch
  // or a backend readiness verdict saying this reference does not count.
  if (isVerified(reference) && !hasDifference(reference)) {
    return {
      title,
      statusLabel: 'Verified',
      tone: 'success',
      iconTone: 'green',
      whatHappened: 'This reference has been accepted.',
      nextStep: 'No action needed.',
      owner: 'Complete',
      primaryAction: null,
      secondaryActions: ['history'],
    };
  }

  if (isRejected(reference)) {
    return {
      title,
      statusLabel: 'Rejected',
      tone: 'danger',
      iconTone: 'red',
      whatHappened: 'This reference cannot be accepted.',
      nextStep: isApplicant ? 'Please add another referee.' : 'Ask the applicant for another referee.',
      owner: 'Applicant',
      primaryAction: needsReplacement(reference) && isApplicant ? 'provide_new' : 'request_replacement',
      secondaryActions: ['history'],
    };
  }

  if (needsReplacement(reference)) {
    return {
      title,
      statusLabel: 'Replacement needed',
      tone: 'danger',
      iconTone: 'red',
      whatHappened: 'A new referee is needed for this slot.',
      nextStep: isApplicant ? 'Add the new referee details so your application can keep moving.' : 'Wait for the applicant to send updated details.',
      owner: 'Applicant',
      primaryAction: isApplicant ? 'provide_new' : null,
      secondaryActions: ['history'],
    };
  }

  if (hasDifference(reference)) {
    return {
      title,
      statusLabel: 'Difference needs review',
      tone: 'warning',
      iconTone: 'amber',
      whatHappened: 'Some details need checking before this reference can be accepted.',
      nextStep: isApplicant ? 'Add a short explanation if asked.' : 'Review the details and decide the next step.',
      owner: isApplicant ? 'You' : 'Admin',
      primaryAction: isAuditor ? null : 'review_reference',
      secondaryActions: ['request_replacement', 'history'],
    };
  }

  if (hasResponse(reference) || RESPONSE_STATUSES.has(status)) {
    return {
      title,
      statusLabel: 'Response received',
      tone: 'info',
      iconTone: 'blue',
      whatHappened: 'The referee has replied.',
      nextStep: isAuditor ? 'Review only.' : 'Review the reference.',
      owner: 'Admin',
      primaryAction: isAuditor ? null : 'review_reference',
      secondaryActions: ['history'],
    };
  }

  // A request record may exist before WhatsApp/email accepts the message.
  // Explicitly empty channel history overrides old sent_at timestamps for
  // modern failed attempts, and a requested-only row is not proof of delivery.
  const channels = reference.request?.channels_delivered;
  const deliveryNotConfirmed = (
    (Array.isArray(channels) && channels.length === 0)
    || (status === 'requested' && !reference.request_sent_at && !reference.request?.sent_at)
  );
  if (deliveryNotConfirmed) {
    return {
      title,
      statusLabel: 'Request not sent',
      tone: 'warning',
      iconTone: 'amber',
      whatHappened: 'A secure reference request was prepared, but no delivery is confirmed.',
      nextStep: 'Send by email or prepare a WhatsApp message and press Send in WhatsApp.',
      owner: 'Admin',
      primaryAction: isAuditor ? null : 'send_request',
      secondaryActions: ['history'],
    };
  }

  if (WAITING_STATUSES.has(status) || reference.request_sent_at || reference.request?.sent_at) {
    return {
      title,
      statusLabel: 'Waiting for referee',
      tone: 'info',
      iconTone: 'blue',
      whatHappened: 'The request has been sent to the referee.',
      nextStep: 'Wait for the referee to reply.',
      owner: 'Referee',
      primaryAction: null,
      secondaryActions: isAuditor ? ['history'] : ['resend_request', 'history'],
    };
  }

  if (!detailsNeeded(reference) && (hasEmail(declared) || hasEmail(reference))) {
    return {
      title,
      statusLabel: 'Ready to send',
      tone: 'ready',
      iconTone: 'teal',
      whatHappened: 'Referee details are ready.',
      nextStep: 'Send the request.',
      owner: 'Admin',
      primaryAction: isAuditor ? null : 'send_request',
      secondaryActions: ['history'],
    };
  }

  return {
    title,
    statusLabel: 'Referee details needed',
    tone: 'muted',
    iconTone: 'slate',
    whatHappened: 'Referee details have not been added yet.',
    nextStep: isApplicant ? 'Add referee details so your application can keep moving.' : 'Ask the applicant to add referee details.',
    owner: 'Applicant',
    primaryAction: isApplicant ? 'provide_new' : null,
    secondaryActions: ['history'],
  };
}

export function containsInternalReferenceTerms(text) {
  return /workspace_id|employee_id|mongo|database|canonical|stagegate|serializer|source[- ]of[- ]truth|lifecycle_status|mismatch_detected/i.test(String(text || ''));
}

export function humanizeReferenceDifferenceText(text) {
  const value = String(text || '').trim();
  if (!value || containsInternalReferenceTerms(value)) {
    return 'Some details in this reference are different from your application. Please explain the difference so we can continue.';
  }

  return value
    .replace(/mismatch(?:es|ed)?/gi, 'difference')
    .replace(/declared/gi, 'entered')
    .replace(/canonical/gi, 'saved')
    .replace(/source[- ]of[- ]truth/gi, 'saved record')
    .replace(/serializer/gi, 'record')
    .replace(/workspace_id|employee_id|database|mongo|stagegate/gi, 'record');
}
